import type { Game, Member } from '../../types/game'
import { assetUrl } from '../../utils/assets'
import { ResourceIcon } from '../shared/ResourceIcon'
import { UiIcon } from './UiIcon'

interface MobilePlayersOverlayProps {
  game: Game
  member: Member
  members: Member[]
  leftMembers: Member[]
}

export function MobilePlayersOverlay({
  game,
  member,
  members,
  leftMembers,
}: MobilePlayersOverlayProps) {
  const currentColor = member.color
  const allMembers = [...leftMembers, ...members]
  const orderedPlayers = [...game.players].sort((left, right) => {
    if (left.color === currentColor && right.color !== currentColor) return -1
    if (right.color === currentColor && left.color !== currentColor) return 1
    return left.color.localeCompare(right.color)
  })

  return (
    <div id="mobile-players-overlay" className="mobile-players-overlay">
      <div id="mobile-players-overlay-list" className="mobile-players-overlay-list">
        {orderedPlayers.map((player) => {
          const playerMember = allMembers.find((candidate) => candidate.color === player.color) ?? null
          const alias = playerMember?.alias ?? `${player.color[0].toUpperCase()}${player.color.slice(1)}`
          const isCurrent = player.color === currentColor
          const hasLeft = leftMembers.some((candidate) => candidate.id === playerMember?.id)

          return (
            <div
              key={player.color}
              className={[
                'mobile-player-card',
                isCurrent ? 'current' : '',
                game.current_player.color === player.color ? 'turn' : '',
              ].filter(Boolean).join(' ')}
            >
              <div className="mobile-player-header">
                <div className="mobile-player-identity">
                  <div className="mobile-player-marker" style={{ background: `var(--${player.color})` }} />
                  <div className="mobile-player-name">{alias}</div>
                  {playerMember?.is_host ? (
                    <img src={assetUrl('images', 'host.svg')} alt="" className="host-icon" title="Host" />
                  ) : null}
                </div>
                <div className="mobile-player-badges">
                  {isCurrent ? <div className="mobile-player-badge current">You</div> : null}
                  {player.has_passed ? <div className="mobile-player-badge passed">Passed</div> : null}
                  {playerMember?.is_bot ? <div className="mobile-player-badge">Bot</div> : null}
                  {hasLeft ? <div className="mobile-player-badge">Left</div> : null}
                </div>
              </div>
              <div className="mobile-player-stats">
                <div className="mobile-player-stat">
                  <ResourceIcon resource="worker" inverted />
                  <div>{player.n_remaining_workers}/{player.n_workers}</div>
                </div>
                <div className="mobile-player-stat">
                  <ResourceIcon resource="egg" inverted />
                  <div>{player.n_eggs}</div>
                </div>
                <div className="mobile-player-stat mobile-player-stat-text">
                  <UiIcon name="flowers_small" className="mobile-player-flower-icon" />
                  <div className="mobile-player-stat-value">{player.flowers.length}</div>
                </div>
              </div>
              <div className="mobile-player-resources">
                {(['honey', 'nectar', 'pollen', 'wax', 'propolis'] as const).map((resource) => (
                  <div key={`${player.color}-${resource}`} className="mobile-player-resource">
                    <ResourceIcon resource={resource} inverted />
                    <div>
                      {player.resources[resource]}
                      {resource === 'honey' && player.required_honey !== undefined && (
                        <span className="honey-required-hint"> ({player.required_honey})</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
